import React, { useCallback, useEffect, useRef } from "react";
import "./DinoRunnerGame.scss";

const CANVAS_WIDTH = 640;
const CANVAS_HEIGHT = 200;
const GROUND_Y = 160;
const DINO_X = 56;
const DINO_WIDTH = 40;
const DINO_HEIGHT = 42;
const GRAVITY = 0.65;
const JUMP_VELOCITY = -11.5;
const INITIAL_SPEED = 6;

type Obstacle = {
  x: number;
  width: number;
  height: number;
};

export default function DinoRunnerGame() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const gameRef = useRef({
    dinoY: GROUND_Y - DINO_HEIGHT,
    dinoVy: 0,
    speed: INITIAL_SPEED,
    score: 0,
    frame: 0,
    obstacles: [] as Obstacle[],
    spawnTimer: 0,
    gameOver: false,
    legFrame: 0,
  });

  const resetGame = useCallback(() => {
    gameRef.current = {
      dinoY: GROUND_Y - DINO_HEIGHT,
      dinoVy: 0,
      speed: INITIAL_SPEED,
      score: 0,
      frame: 0,
      obstacles: [],
      spawnTimer: 0,
      gameOver: false,
      legFrame: 0,
    };
  }, []);

  const jump = useCallback(() => {
    const game = gameRef.current;
    if (game.gameOver) {
      resetGame();
      return;
    }
    const onGround = game.dinoY >= GROUND_Y - DINO_HEIGHT - 0.5;
    if (onGround) {
      game.dinoVy = JUMP_VELOCITY;
    }
  }, [resetGame]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const drawGround = () => {
      ctx.strokeStyle = "#535353";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y + 0.5);
      ctx.lineTo(CANVAS_WIDTH, GROUND_Y + 0.5);
      ctx.stroke();
    };

    const drawDino = (y: number, legFrame: number) => {
      ctx.fillStyle = "#535353";
      ctx.fillRect(DINO_X, y, DINO_WIDTH, DINO_HEIGHT);

      ctx.fillRect(DINO_X + 28, y + 6, 8, 8);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(DINO_X + 32, y + 9, 3, 3);

      const legOffset = legFrame % 2 === 0 ? 0 : 6;
      ctx.fillStyle = "#535353";
      ctx.fillRect(DINO_X + 8, y + DINO_HEIGHT, 8, 6 + legOffset);
      ctx.fillRect(DINO_X + 22, y + DINO_HEIGHT, 8, 6 - legOffset);
    };

    const drawCactus = (obstacle: Obstacle) => {
      ctx.fillStyle = "#1f8a4c";
      ctx.fillRect(obstacle.x, GROUND_Y - obstacle.height, obstacle.width, obstacle.height);
      ctx.fillRect(obstacle.x - 8, GROUND_Y - obstacle.height + 14, 8, 10);
      ctx.fillRect(obstacle.x + obstacle.width, GROUND_Y - obstacle.height + 20, 8, 10);
    };

    const spawnObstacle = () => {
      const height = 28 + Math.floor(Math.random() * 18);
      const width = 14 + Math.floor(Math.random() * 8);
      gameRef.current.obstacles.push({
        x: CANVAS_WIDTH + 20,
        width,
        height,
      });
    };

    const checkCollision = (game: typeof gameRef.current) => {
      const dinoBox = {
        x: DINO_X + 6,
        y: game.dinoY + 4,
        width: DINO_WIDTH - 10,
        height: DINO_HEIGHT - 6,
      };

      return game.obstacles.some((obstacle) => {
        const obstacleBox = {
          x: obstacle.x + 2,
          y: GROUND_Y - obstacle.height,
          width: obstacle.width - 4,
          height: obstacle.height,
        };

        return (
          dinoBox.x < obstacleBox.x + obstacleBox.width &&
          dinoBox.x + dinoBox.width > obstacleBox.x &&
          dinoBox.y < obstacleBox.y + obstacleBox.height &&
          dinoBox.y + dinoBox.height > obstacleBox.y
        );
      });
    };

    const tick = () => {
      const game = gameRef.current;
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      drawGround();

      if (!game.gameOver) {
        game.frame += 1;
        game.legFrame = Math.floor(game.frame / 6);
        game.score = Math.floor(game.frame / 6);
        game.speed = INITIAL_SPEED + Math.min(8, Math.floor(game.frame / 300));

        game.dinoVy += GRAVITY;
        game.dinoY += game.dinoVy;
        if (game.dinoY > GROUND_Y - DINO_HEIGHT) {
          game.dinoY = GROUND_Y - DINO_HEIGHT;
          game.dinoVy = 0;
        }

        game.spawnTimer += 1;
        if (game.spawnTimer > 70 + Math.random() * 50) {
          spawnObstacle();
          game.spawnTimer = 0;
        }

        game.obstacles = game.obstacles
          .map((obstacle) => ({ ...obstacle, x: obstacle.x - game.speed }))
          .filter((obstacle) => obstacle.x + obstacle.width > 0);

        if (checkCollision(game)) {
          game.gameOver = true;
        }
      }

      game.obstacles.forEach(drawCactus);
      drawDino(game.dinoY, game.legFrame);

      ctx.fillStyle = "#535353";
      ctx.font = "bold 16px monospace";
      ctx.fillText(`Puntos: ${game.score}`, CANVAS_WIDTH - 120, 28);

      if (game.gameOver) {
        ctx.fillStyle = "rgba(255, 255, 255, 0.82)";
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        ctx.fillStyle = "#535353";
        ctx.font = "bold 22px monospace";
        ctx.fillText("¡Chocaste!", CANVAS_WIDTH / 2 - 58, CANVAS_HEIGHT / 2 - 10);
        ctx.font = "14px monospace";
        ctx.fillText("Espacio o clic para reiniciar", CANVAS_WIDTH / 2 - 108, CANVAS_HEIGHT / 2 + 18);
      }

      animationRef.current = window.requestAnimationFrame(tick);
    };

    animationRef.current = window.requestAnimationFrame(tick);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Space" || event.code === "ArrowUp") {
        event.preventDefault();
        jump();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      if (animationRef.current) {
        window.cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [jump]);

  return (
    <div className="dino-runner-game">
      <p className="dino-runner-game__hint">
        Salta con <strong>Espacio</strong>, <strong>↑</strong> o haciendo clic en el juego.
      </p>
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="dino-runner-game__canvas"
        onClick={jump}
        role="img"
        aria-label="Juego del dinosaurio mientras esperas"
      />
    </div>
  );
}
